(() => {
  const ROOT_SELECTOR =
    '.fw-rush-desk[data-fw-rush-desk]';

  const FORM_SELECTOR =
    '[data-fw-rush-form]';

  const PRIORITY_VALUE =
    'priority';

  const PAID_URGENCY_VALUES =
    new Set([
      'urgent',
      'critical'
    ]);

  const setInvalid = (
    field,
    invalid
  ) => {
    if (!field) return;

    field.classList.toggle(
      'is-invalid',
      invalid
    );

    if (invalid) {
      field.setAttribute(
        'aria-invalid',
        'true'
      );
    } else {
      field.removeAttribute(
        'aria-invalid'
      );
    }
  };

  const clearValidationSummary = (
    form
  ) => {
    const summary =
      form.querySelector(
        '[data-fw-validation-summary]'
      );

    const list =
      form.querySelector(
        '[data-fw-validation-list]'
      );

    if (summary) {
      summary.hidden = true;
    }

    if (list) {
      list.innerHTML = '';
    }
  };

  const showValidationError = (
    form,
    message
  ) => {
    const summary =
      form.querySelector(
        '[data-fw-validation-summary]'
      );

    const list =
      form.querySelector(
        '[data-fw-validation-list]'
      );

    if (
      !summary ||
      !list
    ) {
      return;
    }

    list.innerHTML = '';

    const item =
      document.createElement('li');

    item.textContent =
      message;

    list.appendChild(item);
    summary.hidden = false;
  };

  const getDetectedTimeZone = () => {
    try {
      return (
        Intl
          .DateTimeFormat()
          .resolvedOptions()
          .timeZone ||
        ''
      );
    } catch (error) {
      return '';
    }
  };

  const getDatePartsInTimeZone = (
    date,
    timeZone
  ) => {
    try {
      const formatter =
        new Intl.DateTimeFormat(
          'en-US',
          {
            timeZone,
            year: 'numeric',
            month: '2-digit',
            day: '2-digit',
            hour: '2-digit',
            minute: '2-digit',
            hourCycle: 'h23'
          }
        );

      const parts = {};

      formatter
        .formatToParts(date)
        .forEach((part) => {
          if (part.type !== 'literal') {
            parts[part.type] =
              Number(part.value);
          }
        });

      return parts;
    } catch (error) {
      return null;
    }
  };

  const getTodayInTimeZone = (
    timeZone
  ) => {
    const parts =
      getDatePartsInTimeZone(
        new Date(),
        timeZone
      );

    if (!parts) {
      const now =
        new Date();

      const year =
        now.getFullYear();

      const month =
        String(
          now.getMonth() + 1
        ).padStart(2, '0');

      const day =
        String(
          now.getDate()
        ).padStart(2, '0');

      return `${year}-${month}-${day}`;
    }

    return (
      String(parts.year) +
      '-' +
      String(parts.month).padStart(2, '0') +
      '-' +
      String(parts.day).padStart(2, '0')
    );
  };

  const zonedWallTimeToDate = (
    dateValue,
    timeValue,
    timeZone
  ) => {
    const dateMatch =
      String(dateValue || '')
        .match(
          /^(\d{4})-(\d{2})-(\d{2})$/
        );

    const timeMatch =
      String(timeValue || '')
        .match(
          /^(\d{2}):(\d{2})$/
        );

    if (
      !dateMatch ||
      !timeMatch ||
      !timeZone
    ) {
      return null;
    }

    const target = {
      year: Number(dateMatch[1]),
      month: Number(dateMatch[2]),
      day: Number(dateMatch[3]),
      hour: Number(timeMatch[1]),
      minute: Number(timeMatch[2])
    };

    const targetAsUtc =
      Date.UTC(
        target.year,
        target.month - 1,
        target.day,
        target.hour,
        target.minute,
        0
      );

    let timestamp =
      targetAsUtc;

    for (
      let iteration = 0;
      iteration < 4;
      iteration += 1
    ) {
      const actual =
        getDatePartsInTimeZone(
          new Date(timestamp),
          timeZone
        );

      if (!actual) {
        return null;
      }

      const actualAsUtc =
        Date.UTC(
          actual.year,
          actual.month - 1,
          actual.day,
          actual.hour,
          actual.minute,
          0
        );

      const difference =
        targetAsUtc -
        actualAsUtc;

      timestamp +=
        difference;

      if (difference === 0) {
        break;
      }
    }

    const finalParts =
      getDatePartsInTimeZone(
        new Date(timestamp),
        timeZone
      );

    if (
      !finalParts ||
      finalParts.year !== target.year ||
      finalParts.month !== target.month ||
      finalParts.day !== target.day ||
      finalParts.hour !== target.hour ||
      finalParts.minute !== target.minute
    ) {
      return null;
    }

    return new Date(timestamp);
  };

  const initForm = (form) => {
    if (
      !form ||
      form.dataset.fwRushReady ===
        'true'
    ) {
      return;
    }

    form.dataset.fwRushReady =
      'true';

    const email =
      form.querySelector(
        '[data-fw-field="email"]'
      );

    const phone =
      form.querySelector(
        '[data-fw-field="phone"]'
      );

    const urgency =
      form.querySelector(
        '[data-fw-rush-urgency]'
      );

    const requestedDate =
      form.querySelector(
        '[data-fw-rush-date]'
      );

    const requestedTime =
      form.querySelector(
        '[data-fw-rush-time]'
      );

    const requestedTimeWrapper =
      form.querySelector(
        '[data-fw-rush-time-wrapper]'
      );

    const timeCallout =
      form.querySelector(
        '[data-fw-rush-time-callout]'
      );

    const priorityTimeNote =
      form.querySelector(
        '[data-fw-rush-priority-time-note]'
      );

    const timeZoneWrapper =
      form.querySelector(
        '[data-fw-rush-timezone-wrapper]'
      );

    const timeZone =
      form.querySelector(
        '[data-fw-rush-timezone]'
      );

    const detectedTimeZoneText =
      form.querySelector(
        '[data-fw-rush-detected-timezone]'
      );

    const detectedTimeZone =
      getDetectedTimeZone();

    const detectedOptionPrefix =
      (
        timeZone?.dataset
          .fwDetectedOptionPrefix ||
        'Detected Time Zone'
      ).trim();

    const detectedMessageTemplate =
      (
        timeZone?.dataset
          .fwDetectedMessageTemplate ||
        'Detected from your device: {timezone}. Change it if this is not your local time zone.'
      ).trim();

    const undetectedMessage =
      (
        timeZone?.dataset
          .fwUndetectedMessage ||
        'Your device time zone could not be detected. Select your local time zone if you enter a requested completion time.'
      ).trim();

    const ensureDetectedTimeZone = () => {
      if (!timeZone) {
        return;
      }

      if (detectedTimeZone) {
        let matchingOption =
          Array.from(
            timeZone.options
          )
            .find(
              (option) =>
                option.value ===
                detectedTimeZone
            );

        if (!matchingOption) {
          matchingOption =
            document.createElement(
              'option'
            );

          matchingOption.value =
            detectedTimeZone;

          matchingOption.textContent =
            `${detectedOptionPrefix} — ${detectedTimeZone}`;

          timeZone.insertBefore(
            matchingOption,
            timeZone.options[1] ||
              null
          );
        }

        timeZone.value =
          detectedTimeZone;

        if (detectedTimeZoneText) {
          const selectedLabel =
            (
              matchingOption.textContent ||
              detectedTimeZone
            ).trim();

          detectedTimeZoneText.textContent =
            detectedMessageTemplate
              .replaceAll(
                '{timezone}',
                selectedLabel
              );
        }

        return;
      }

      if (detectedTimeZoneText) {
        detectedTimeZoneText.textContent =
          undetectedMessage;
      }
    };

    const syncDateMinimum = () => {
      if (!requestedDate) {
        return;
      }

      const zone =
        timeZone?.value ||
        detectedTimeZone;

      requestedDate.min =
        getTodayInTimeZone(
          zone || undefined
        );
    };

    const syncUrgency = () => {
      if (
        !urgency ||
        !requestedTime
      ) {
        return;
      }

      const value =
        urgency.value;

      const paidUrgency =
        PAID_URGENCY_VALUES.has(
          value
        );

      if (paidUrgency) {
        requestedTime.disabled =
          false;

        requestedTime.required =
          false;

        requestedTime.removeAttribute(
          'data-fw-required'
        );

        requestedTimeWrapper
          ?.classList
          .remove(
            'is-disabled'
          );

        if (timeCallout) {
          timeCallout.hidden =
            false;
        }

        if (priorityTimeNote) {
          priorityTimeNote.hidden =
            true;
        }

        if (timeZoneWrapper) {
          timeZoneWrapper.hidden =
            false;
        }

        setInvalid(
          requestedTime,
          false
        );

        setInvalid(
          timeZone,
          false
        );

        syncDateMinimum();
        return;
      }

      requestedTime.value = '';

      requestedTime.disabled =
        true;

      requestedTime.required =
        false;

      requestedTime.removeAttribute(
        'data-fw-required'
      );

      requestedTimeWrapper
        ?.classList
        .add(
          'is-disabled'
        );

      if (timeCallout) {
        timeCallout.hidden =
          true;
      }

      if (priorityTimeNote) {
        priorityTimeNote.hidden =
          false;
      }

      if (timeZoneWrapper) {
        timeZoneWrapper.hidden =
          true;
      }

      setInvalid(
        requestedTime,
        false
      );

      setInvalid(
        timeZone,
        false
      );

      syncDateMinimum();
    };

    const clearContactError = () => {
      if (
        email?.value.trim() ||
        phone?.value.trim()
      ) {
        setInvalid(
          email,
          false
        );

        setInvalid(
          phone,
          false
        );
      }
    };

    const validateRushTiming = () => {
      const zone =
        timeZone?.value ||
        detectedTimeZone ||
        '';

      const today =
        getTodayInTimeZone(
          zone || undefined
        );

      if (
        requestedDate?.value &&
        requestedDate.value < today
      ) {
        setInvalid(
          requestedDate,
          true
        );

        return {
          field: requestedDate,
          message:
            'Requested Completion Date cannot be in the past.'
        };
      }

      setInvalid(
        requestedDate,
        false
      );

      const paidUrgency =
        PAID_URGENCY_VALUES.has(
          urgency?.value
        );

      if (
        !paidUrgency ||
        !requestedTime?.value
      ) {
        setInvalid(
          requestedTime,
          false
        );

        setInvalid(
          timeZone,
          false
        );

        return null;
      }

      if (!zone) {
        setInvalid(
          timeZone,
          true
        );

        return {
          field: timeZone,
          message:
            'Select your local time zone when requesting a specific completion time.'
        };
      }

      const requestedInstant =
        zonedWallTimeToDate(
          requestedDate?.value,
          requestedTime.value,
          zone
        );

      if (!requestedInstant) {
        setInvalid(
          requestedTime,
          true
        );

        return {
          field: requestedTime,
          message:
            'That requested time is not valid in the selected time zone. Choose another time.'
        };
      }

      if (
        requestedInstant.getTime() <=
        Date.now()
      ) {
        setInvalid(
          requestedTime,
          true
        );

        return {
          field: requestedTime,
          message:
            'Requested Completion Date and Time cannot be in the past.'
        };
      }

      setInvalid(
        requestedTime,
        false
      );

      setInvalid(
        timeZone,
        false
      );

      return null;
    };

    urgency?.addEventListener(
      'change',
      syncUrgency
    );

    timeZone?.addEventListener(
      'change',
      () => {
        setInvalid(
          timeZone,
          false
        );

        syncDateMinimum();
      }
    );

    requestedDate?.addEventListener(
      'change',
      () => {
        setInvalid(
          requestedDate,
          false
        );
      }
    );

    requestedTime?.addEventListener(
      'input',
      () => {
        setInvalid(
          requestedTime,
          false
        );
      }
    );

    email?.addEventListener(
      'input',
      clearContactError
    );

    phone?.addEventListener(
      'input',
      clearContactError
    );

    form.addEventListener(
      'reset',
      () => {
        window.setTimeout(
          () => {
            ensureDetectedTimeZone();
            syncUrgency();

            setInvalid(
              email,
              false
            );

            setInvalid(
              phone,
              false
            );

            setInvalid(
              requestedDate,
              false
            );

            setInvalid(
              requestedTime,
              false
            );

            setInvalid(
              timeZone,
              false
            );
          },
          0
        );
      }
    );

    form.addEventListener(
      'submit',
      (event) => {
        syncUrgency();

        const hasEmail =
          Boolean(
            email?.value.trim()
          );

        const hasPhone =
          Boolean(
            phone?.value.trim()
          );

        if (
          !hasEmail &&
          !hasPhone
        ) {
          event.preventDefault();
          event.stopImmediatePropagation();

          setInvalid(
            email,
            true
          );

          setInvalid(
            phone,
            true
          );

          showValidationError(
            form,
            'Enter an email address or phone number.'
          );

          email?.focus();
          return;
        }

        const timingError =
          validateRushTiming();

        if (timingError) {
          event.preventDefault();
          event.stopImmediatePropagation();

          showValidationError(
            form,
            timingError.message
          );

          timingError.field?.focus();
          return;
        }

        clearValidationSummary(form);

        if (
          urgency?.value ===
          PRIORITY_VALUE &&
          requestedTime
        ) {
          requestedTime.value = '';
        }
      },
      true
    );

    ensureDetectedTimeZone();
    syncDateMinimum();
    syncUrgency();
  };

  const initRoot = (root) => {
    const form =
      root.querySelector(
        FORM_SELECTOR
      );

    initForm(form);
  };

  const initWithin = (
    scope = document
  ) => {
    if (
      scope.matches?.(
        ROOT_SELECTOR
      )
    ) {
      initRoot(scope);
    }

    scope
      .querySelectorAll?.(
        ROOT_SELECTOR
      )
      .forEach(initRoot);
  };

  if (
    document.readyState ===
    'loading'
  ) {
    document.addEventListener(
      'DOMContentLoaded',
      () => initWithin(),
      { once: true }
    );
  } else {
    initWithin();
  }

  document.addEventListener(
    'shopify:section:load',
    (event) => {
      initWithin(event.target);
    }
  );
})();